import { useState, useEffect } from 'react';
import { extractAttributesList } from '@/utils/api-client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  goldenDocService,
  type GoldenDocument,
  type GoldenDocCreateData,
  type ContentfulProgram,
  type ContentfulProgramUnit,
  type ContentfulAdditionalAiContentEntry,
} from '@/services/golden-doc-service';
import { domainService, type Domain } from '@/services/domain-service';
import {
  Header,
  ContentContainer,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  Spinner,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Checkbox,
  Label,
  toast,
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Star, RefreshCw, Edit, MoreHorizontal } from '@/lib/icons';
import { format } from 'date-fns';

type EditForm = {
  title: string;
  priority: number;
  is_active: boolean;
  domain_id: string;
};

export function KaiyaGoldenDocsPage() {
  const { setShowPageHeader } = usePageContext();
  const queryClient = useQueryClient();
  const [selectedDomainId, setSelectedDomainId] = useState<string>('');
  const [docToDelete, setDocToDelete] = useState<GoldenDocument | null>(null);
  const [docToEdit, setDocToEdit] = useState<GoldenDocument | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ title: '', priority: 0, is_active: true, domain_id: '' });
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState<GoldenDocCreateData>({
    title: '',
    source_type: 'manual',
    content: '',
    domain_id: '',
    priority: 0,
  });
  const [selectedProgramSlug, setSelectedProgramSlug] = useState<string>('');

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  const { data: domainsData } = useQuery({
    queryKey: ['domains'],
    queryFn: () => domainService.list(),
  });

  const domains: Domain[] = extractAttributesList(domainsData);

  const { data, isLoading } = useQuery({
    queryKey: ['goldenDocs', selectedDomainId],
    queryFn: () => goldenDocService.list(selectedDomainId ? { domain_id: selectedDomainId } : {}),
  });

  const docs: GoldenDocument[] = extractAttributesList(data);

  const { data: programsData } = useQuery({
    queryKey: ['contentfulPrograms'],
    queryFn: () => goldenDocService.listPrograms(),
  });

  const programs: ContentfulProgram[] = programsData?.data ?? [];

  const { data: programUnitsData, isLoading: isLoadingUnits } = useQuery({
    queryKey: ['contentfulProgramUnits', selectedProgramSlug],
    queryFn: () => goldenDocService.listProgramUnits(selectedProgramSlug),
    enabled: !!selectedProgramSlug,
  });

  const programUnits: ContentfulProgramUnit[] = programUnitsData?.data ?? [];

  const { data: additionalAiContentData } = useQuery({
    queryKey: ['contentfulAdditionalAiContent'],
    queryFn: () => goldenDocService.listAdditionalAiContent(),
  });

  const additionalAiContentEntries: ContentfulAdditionalAiContentEntry[] = additionalAiContentData?.data ?? [];

  const createMutation = useMutation({
    mutationFn: (data: GoldenDocCreateData) => goldenDocService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goldenDocs'] });
      toast.success('Golden document created');
      resetCreateForm();
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to create'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => goldenDocService.destroy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goldenDocs'] });
      toast.success('Golden document deleted');
      setDocToDelete(null);
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to delete'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<GoldenDocCreateData> }) =>
      goldenDocService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goldenDocs'] });
      toast.success('Golden document updated');
      setDocToEdit(null);
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to update'),
  });

  const refreshMutation = useMutation({
    mutationFn: (id: string) => goldenDocService.refreshContent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goldenDocs'] });
      toast.success('Content refreshed from Contentful');
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to refresh'),
  });

  const openEdit = (doc: GoldenDocument) => {
    setDocToEdit(doc);
    setEditForm({
      title: doc.title,
      priority: doc.priority,
      is_active: doc.is_active,
      domain_id: doc.domain_id,
    });
  };

  const resetCreateForm = () => {
    setShowCreateDialog(false);
    setCreateForm({ title: '', source_type: 'manual', content: '', domain_id: '', priority: 0 });
    setSelectedProgramSlug('');
  };

  const handleSourceTypeChange = (sourceType: 'contentful' | 'manual' | 'additional_ai_content') => {
    setCreateForm(prev => ({
      ...prev,
      source_type: sourceType,
      title: '',
      content: '',
      program_slug: undefined,
      unit_slug: undefined,
      unit_content_type: undefined,
      contentful_entry_id: undefined,
    }));
    setSelectedProgramSlug('');
  };

  const handleProgramChange = (programSlug: string) => {
    setSelectedProgramSlug(programSlug);
    setCreateForm(prev => ({
      ...prev,
      program_slug: programSlug || undefined,
      unit_slug: undefined,
      unit_content_type: undefined,
      title: '',
    }));
  };

  const handleUnitChange = (unitSlug: string) => {
    const unit = programUnits.find(u => u.slug === unitSlug);
    if (unit) {
      const program = programs.find(p => p.program_slug === selectedProgramSlug);
      const displayTitle = program ? `${program.program_name} > ${unit.title}` : unit.title;
      setCreateForm(prev => ({
        ...prev,
        unit_slug: unit.slug,
        unit_content_type: unit.content_type,
        title: displayTitle,
      }));
    } else {
      setCreateForm(prev => ({
        ...prev,
        unit_slug: undefined,
        unit_content_type: undefined,
        title: '',
      }));
    }
  };

  const handleAdditionalAiContentChange = (entryId: string) => {
    const entry = additionalAiContentEntries.find(e => e.id === entryId);
    setCreateForm(prev => ({
      ...prev,
      contentful_entry_id: entry?.id,
      title: entry?.title ?? '',
    }));
  };

  const sourceTypeBadge = (type: string) => {
    switch (type) {
      case 'contentful': return <Badge variant="secondary">Contentful</Badge>;
      case 'manual': return <Badge variant="outline">Manual</Badge>;
      case 'additional_ai_content': return <Badge variant="secondary">Additional AI Content</Badge>;
      default: return <Badge variant="outline">{type}</Badge>;
    }
  };

  const isCreateDisabled = () => {
    if (createMutation.isPending || !createForm.domain_id) return true;
    if (createForm.source_type === 'contentful') return !createForm.unit_slug;
    if (createForm.source_type === 'additional_ai_content') return !createForm.contentful_entry_id;
    return !createForm.title;
  };

  return (
    <>
      <Header
        title="Golden Documents"
        description="Curate authoritative content that should always surface for specific domains"
      />

      <ContentContainer>
        {/* Domain filter + Add button */}
        <div className="flex items-center justify-between mb-4">
          <Select
            value={selectedDomainId || 'all'}
            onValueChange={(v) => setSelectedDomainId(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Domains" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Domains</SelectItem>
              {domains.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Golden Document
          </Button>
        </div>

        {isLoading ? (
          <Empty><Spinner /></Empty>
        ) : docs.length === 0 ? (
          <Empty>
            <EmptyIcon><Star className="h-12 w-12" /></EmptyIcon>
            <EmptyTitle>No golden documents</EmptyTitle>
            <EmptyDescription>
              Add authoritative content references that should always surface for specific domains.
            </EmptyDescription>
          </Empty>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Priority</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Domain</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="w-[50px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {docs.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-mono text-sm">{doc.priority}</TableCell>
                      <TableCell className="max-w-[300px]">
                        <div className="font-medium truncate">{doc.title}</div>
                        {doc.content_preview && (
                          <div className="text-xs text-muted-foreground truncate">
                            {doc.content_preview}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{doc.domain_name}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {sourceTypeBadge(doc.source_type)}
                          {doc.is_stale && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              Stale
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={doc.is_active ? 'default' : 'secondary'}>
                          {doc.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {doc.created_at ? format(new Date(doc.created_at), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(doc)}>
                              <Edit className="h-4 w-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            {(doc.source_type === 'contentful' || doc.source_type === 'additional_ai_content') && (
                              <DropdownMenuItem
                                onClick={() => refreshMutation.mutate(doc.id)}
                                disabled={refreshMutation.isPending}
                              >
                                <RefreshCw className="h-4 w-4 mr-2" /> Refresh Content
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDocToDelete(doc)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </ContentContainer>

      {/* Create dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) resetCreateForm(); }}>
        <DialogContent className="sm:!max-w-3xl">
          <DialogHeader>
            <DialogTitle>Add Golden Document</DialogTitle>
            <DialogDescription>
              Create a curated reference document for a specific domain.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Domain</label>
              {domains.length === 0 ? (
                <div className="mt-1 p-3 border rounded-md bg-muted text-sm text-muted-foreground">
                  No domains found. <Link to="/kaiya-domains" className="text-primary underline">Create one in Domains</Link> first.
                </div>
              ) : (
                <Select
                  value={createForm.domain_id || undefined}
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, domain_id: v }))}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue placeholder="Select a domain..." />
                  </SelectTrigger>
                  <SelectContent>
                    {domains.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Source Type</label>
              <Select
                value={createForm.source_type}
                onValueChange={(v) => handleSourceTypeChange(v as 'contentful' | 'manual' | 'additional_ai_content')}
              >
                <SelectTrigger className="w-full mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="contentful">Contentful Program Page</SelectItem>
                  <SelectItem value="additional_ai_content">Additional AI Content</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {createForm.source_type === 'contentful' ? (
              <>
                <div>
                  <label className="text-sm font-medium">Program</label>
                  <Select
                    value={selectedProgramSlug || undefined}
                    onValueChange={handleProgramChange}
                  >
                    <SelectTrigger className="w-full mt-1">
                      <SelectValue placeholder="Select a program..." />
                    </SelectTrigger>
                    <SelectContent>
                      {programs.map((p) => (
                        <SelectItem key={p.program_slug} value={p.program_slug}>
                          {p.program_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedProgramSlug && (
                  <div>
                    <label className="text-sm font-medium">Page</label>
                    {isLoadingUnits ? (
                      <div className="mt-1 px-3 py-2 text-sm text-muted-foreground">Loading pages...</div>
                    ) : (
                      <Select
                        value={createForm.unit_slug || undefined}
                        onValueChange={handleUnitChange}
                      >
                        <SelectTrigger className="w-full mt-1">
                          <SelectValue placeholder="Select a page..." />
                        </SelectTrigger>
                        <SelectContent>
                          {programUnits.map((u) => {
                            const indent = '\u00A0\u00A0'.repeat((u.level - 1) * 2);
                            return (
                              <SelectItem key={u.slug} value={u.slug}>
                                {indent}{u.title}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
                {createForm.unit_slug && (
                  <div className="p-3 bg-muted rounded-md">
                    <div className="text-xs text-muted-foreground mb-1">Selected page</div>
                    <div className="text-sm font-medium">{createForm.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Content will be fetched automatically from Contentful.
                    </div>
                  </div>
                )}
              </>
            ) : createForm.source_type === 'additional_ai_content' ? (
              <>
                <div>
                  <label className="text-sm font-medium">Entry</label>
                  <Select
                    value={createForm.contentful_entry_id || undefined}
                    onValueChange={handleAdditionalAiContentChange}
                  >
                    <SelectTrigger className="w-full mt-1">
                      <SelectValue placeholder="Select an entry..." />
                    </SelectTrigger>
                    <SelectContent>
                      {additionalAiContentEntries.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {createForm.contentful_entry_id && (
                  <div className="p-3 bg-muted rounded-md">
                    <div className="text-xs text-muted-foreground mb-1">Selected entry</div>
                    <div className="text-sm font-medium">{createForm.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Content will be fetched automatically from Contentful.
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <label className="text-sm font-medium">Title</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                    value={createForm.title}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Content</label>
                  <textarea
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                    rows={4}
                    value={createForm.content || ''}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, content: e.target.value }))}
                  />
                </div>
              </>
            )}
            <div>
              <label className="text-sm font-medium">Priority (lower = higher)</label>
              <input
                type="number"
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                value={createForm.priority}
                onChange={(e) => setCreateForm(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetCreateForm}>Cancel</Button>
            <Button
              onClick={() => createMutation.mutate(createForm)}
              disabled={isCreateDisabled()}
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!docToEdit} onOpenChange={() => setDocToEdit(null)}>
        <DialogContent className="sm:!max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Golden Document</DialogTitle>
            <DialogDescription>
              Update this golden document's properties.
            </DialogDescription>
          </DialogHeader>
          {docToEdit && (
            <div className="space-y-4 py-4">
              {docToEdit.source_type === 'manual' && (
                <div>
                  <label className="text-sm font-medium">Title</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                    value={editForm.title}
                    onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
              )}
              <div>
                <label className="text-sm font-medium">Domain</label>
                <Select
                  value={editForm.domain_id}
                  onValueChange={(v) => setEditForm(prev => ({ ...prev, domain_id: v }))}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {domains.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Priority (lower = higher)</label>
                  <input
                    type="number"
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                    value={editForm.priority}
                    onChange={(e) => setEditForm(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="flex items-end">
                  <Label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={editForm.is_active}
                      onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, is_active: !!checked }))}
                    />
                    Active
                  </Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocToEdit(null)}>Cancel</Button>
            <Button
              onClick={() => docToEdit && updateMutation.mutate({
                id: docToEdit.id,
                data: {
                  ...(docToEdit.source_type === 'manual' ? { title: editForm.title } : {}),
                  priority: editForm.priority,
                  is_active: editForm.is_active,
                  domain_id: editForm.domain_id,
                },
              })}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!docToDelete} onOpenChange={() => setDocToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Golden Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{docToDelete?.title}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocToDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => docToDelete && deleteMutation.mutate(docToDelete.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
